import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import GetSOCReportFile from "src/api/GetSOCReportFile";
import GetSOCReportList from "src/api/GetSOCReportList";
import useAuth from "src/hooks/useAuth";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()(theme => ({
  table: {
    minWidth: 650
  },
}));


const SOCReportTable = ({ downloadReportsPermission }) => {
  const { getAccessToken } = useAuth();
  const { classes } = useStyles();
  const [socFiles, setSocFiles] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloadingFile, setDownloadingFile] = useState('');
  const { t } = useTranslation();
  const theme = useTheme();
  const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));

  const MobileSkeletonCard = () => {
    return (
      <Card
        variant="outlined"
        sx={{
          backgroundColor: 'var(--neutral-3)',
          borderColor: 'var(--color-border)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(2)
          }}
        >
          <Skeleton
            height={24}
            width={150}
            variant="rounded"
            sx={{
              backgroundColor: 'var(--neutral-7)',
              border: '1px solid var(--color-border)'
            }}
          />
          <Skeleton
            height={32}
            width={32}
            variant="rounded"
            sx={{
              backgroundColor: 'var(--neutral-7)',
              border: '1px solid var(--color-border)'
            }}
          />
        </Box>
        <CardContent>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0, width: '50%' }}
                  align="left"
                >
                  <Skeleton
                    height={20}
                    width={80}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Skeleton
                    height={20}
                    width={'200px'}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow> 
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Skeleton
                    height={20}
                    width={80}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Skeleton
                    height={20}
                    width={'200px'}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Skeleton
                    height={20}
                    width={80}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Skeleton
                    height={20}
                    width={'200px'}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="left"
                >
                  <Skeleton
                    height={20}
                    width={80}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                  align="right"
                >
                  <Skeleton
                    height={20}
                    width={'200px'}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  const MobileCard = props => {
    const { card, index, downloadFile } = props;
    return (
      <Card
        variant="outlined"
        sx={{
          margin: '40px 5px',
          backgroundColor: 'var(--neutral-3)',
          borderColor: 'var(--color-border)'
        }}
        key={index}
      >
      <CardHeader
        sx={{ margin: theme.spacing(0.5) }}
        title={<Typography>{card.name}</Typography>}
        action={
          <IconButton
            onClick={() => downloadFile(card.fileName)}
            loading={downloadingFile === card.fileName}
            loadingIndicator={<CircularProgress size={16} />}
          >
            <Download size={16} />
          </IconButton>
        }
      >
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="left"
              >
                <Typography>{t('type')}</Typography>
              </TableCell>
                <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="right"
              >
                <Typography>{!!card.type ? card.type : '-'}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
                <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="left"
              >
                <Typography>Coverage</Typography>
              </TableCell>
                <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="right"
              >
                <Typography>{!!card.coverage ? card.coverage : '-'}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="left"
              >
                <Typography>Issued By</Typography>
              </TableCell>
                <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="right"
              >
                <Typography>{card.issuedBy}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="left"
              >
                <Typography>Issued On</Typography>
              </TableCell>
                <TableCell
                sx={{ paddingLeft: 0, paddingRight: 0}}
                align="right"
              >
                <Typography>{card.issuedOn}</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
    )
  }

  const fetchReportList = useCallback(async () => {
    setLoading(true)
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) {
      console.log('Error: ' + accessToken);
    }

    const {data, success} = await GetSOCReportList(
      accessToken,
    );

    if (!!success && !!data?.files) {
      const reports = data?.files.filter(file => !!file.type);
      const sortedReports = reports.sort((a, b) => dayjs(a.issuedOn).valueOf() - dayjs(b.issuedOn).valueOf()).reverse();
      const faqFile = data?.files.filter(file => !file.type)[0];
      const allFiles = [...sortedReports, faqFile];
      setSocFiles(allFiles)
      setLoading(false)
    } else {
      setError(data)
      setLoading(false)
    }
  });

  const downloadReportFile = async (fileName) => {
    setDownloadingFile(fileName)
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) {
      console.log('Error: ' + accessToken);
    }
  
      const { success, data } = await GetSOCReportFile(fileName, accessToken);
      if (!!success && !!data) {
        const blob = new Blob([data], { type: 'application/octet-stream' })
        const reportFileUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = reportFileUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(reportFileUrl)
      } else {
        setError(data)
      }

      setDownloadingFile('')
   }

  useEffect(() => {
    fetchReportList();
  }, [])

  return   (<Card
              style={{
                  backgroundColor: 'var(--neutral-2)',
              }}
            >
              { mediumDevice ? <>
                {
                  loading ? (
                    <MobileSkeletonCard />
                  ) : socFiles.length > 0 ? (
                    socFiles.map((file, index) => {
                      return <MobileCard card={file} index={index} downloadFile={downloadReportFile} />
                    })
                  ) : !socFiles.length && !error ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyItems="center"
                      alignItems='center'
                      style={{ background: 'var(--color-background)'}}
                    >
                      <div style={{ position: "relative"}}>
                        <p style={{position: "absolute", left: 160, top: 280, fontFamily: 'var(--font-family)', fontSize: 16, fontWeight: 600, color: "#e6e5e8"}}>No SOC report files yet</p>
                        <p style={{position: "absolute", left: 150, top: 310, fontFamily: 'var(--font-family)', fontSize: 14, fontWeight: 400, color: '#9AA4B2'}}>This will get populated later!</p>
                        <div style={{position: "absolute", width: 48, height: 48, left: 217, top: 217, border: '1px solid #364152', borderRadius: 'var(--radius-l)'}}></div>
                        <svg viewBox="0 0 25 24" style={{position: "absolute", left: 229, top: 230}} width="25" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21.5 21L18.0001 17.5M20.5 11.5C20.5 16.1944 16.6944 20 12 20C7.30558 20 3.5 16.1944 3.5 11.5C3.5 6.80558 7.30558 3 12 3C16.6944 3 20.5 6.80558 20.5 11.5Z" stroke="#CDD5DF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <svg width="480" height="480" viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="240" cy="240" r="47.5" stroke="#202939"/>
                          <circle cx="240" cy="240" r="79.5" stroke="#202939"/>
                          <circle cx="240" cy="240" r="111.5" stroke="#202939"/>
                          <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.5} />
                          <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.4} />
                          <circle cx="240" cy="240" r="175.5" stroke="#202939" opacity={0.3} />
                          <circle cx="240" cy="240" r="207.5" stroke="#202939" opacity={0.2} />
                          <circle cx="240" cy="240" r="239.5" stroke="#202939" opacity={0.1} />
                        </svg>
                      </div>
                    </Box>
                  ) : error ? <Card
                        variant="outlined"
                        sx={{
                          margin: '40px 5px',
                          backgroundColor: 'var(--neutral-3)',
                          borderColor: 'var(--color-border)'
                        }}
                      >
                      <CardContent>
                        <Box sx={{ height: 200, alignContent: 'center' }}>
                          <Typography style={{ textAlign: 'center'}}>{ error }</Typography>
                        </Box>
                      </CardContent>
                    </Card> : null
                }
              </> :
              <>
                <TableContainer
                    style={{
                        backgroundColor: 'var(--neutral-2)',
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0,
                        borderTop: '2px solid var(--color-border)',
                        position: 'relative',
                        maxHeight: 'calc(100vh - 285px)',
                    }}
                    component={Paper}
                >
                <Table className={classes.table} size="small" stickyHeader>
                    <TableHead>
                        <TableRow
                            style={{
                                height: '50px',
                                whiteSpace: 'nowrap',
                                backgroundColor: 'var(--color-surface-raised)'
                            }}
                        >
                            <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                File Name
                            </TableCell>
                            <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                {t('type')}
                            </TableCell>
                            <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                Coverage
                            </TableCell>
                            <TableCell style={{ backgroundColor: 'var(--color-surface-raised)' }} align="left">
                                Issued By
                            </TableCell>
                            <TableCell style={{ backgroundColor: 'var(--color-surface-raised)'}} align="left">
                                Issued On
                            </TableCell>
                            <TableCell style={{ backgroundColor: 'var(--color-surface-raised)'}}  width={'64px'}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                  {loading ? <TableRow style={{ height: '50px'}}>
                          <TableCell align="left" style={{ border: 'none '}}>
                              <Skeleton variant="rounded" height={20} />
                          </TableCell>
                          <TableCell align="left" style={{ border: 'none '}}>
                            <Skeleton variant="rounded" height={20} />
                          </TableCell>
                          <TableCell align="left" style={{ border: 'none '}}>
                            <Skeleton variant="rounded" height={20} />
                          </TableCell>
                          <TableCell align="left" style={{ border: 'none '}}>
                            <Skeleton variant="rounded" height={20} />
                          </TableCell>
                          <TableCell align="left" style={{ border: 'none '}}>
                            <Skeleton variant="rounded" height={20} />
                          </TableCell>
                          <TableCell align="center" style={{ border: 'none '}}>
                            <Skeleton variant="rounded" height={20} />
                          </TableCell>
                          </TableRow> : !!error && !loading ? <TableRow>
                              <TableCell style={{ border: 'none ', height: 200 }} align="center" colSpan={6}>
                                  { error }
                            </TableCell>
                      </TableRow> :
                    socFiles.length ? socFiles.map((file, index) => {
                          return <TableRow key={index} style={{ height: '50px'}}>
                            <TableCell align="left">{file.name}</TableCell>
                            <TableCell align="left">{file.type}</TableCell>
                            <TableCell align="left">{file.coverage}</TableCell>
                            <TableCell align="left">{file.issuedBy}</TableCell>
                            <TableCell align="left">{file.issuedOn}</TableCell>
                            <TableCell align="center"> {!!downloadReportsPermission ? 
                              <IconButton 
                                onClick={() => downloadReportFile(file.fileName)}
                                loading={downloadingFile === file.fileName}
                                loadingIndicator={<CircularProgress size={16} />}
                              ><Download size={16} /></IconButton>
                              :
                              <Tooltip title={
                                <Typography variant='h6' style={{ padding: '10px 0 10px 30px' }}>
                                  You must have Group Owner access to download SOC files.
                                </Typography>
                              }>
                                <span>
                                  <IconButton disabled><Download size={16} /></IconButton>
                                </span>
                              </Tooltip>}
                            </TableCell>
                          </TableRow> 
                          })
                        :
                      <TableRow>
                        <TableCell colSpan={14} style={{ padding: 0, borderBottom: 'none'}}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            justifyItems="center"
                            alignItems='center'
                            style={{ background: 'var(--color-background)'}}
                          >
                            <div style={{ position: "relative"}}>
                              <p style={{position: "absolute", left: 164, top: 280, fontFamily: 'var(--font-family)', fontSize: 16, fontWeight: 600, color: "#e6e5e8"}}>No SOC reports yet</p>
                              <p style={{position: "absolute", left: 150, top: 310, fontFamily: 'var(--font-family)', fontSize: 14, fontWeight: 400, color: '#9AA4B2'}}>This will get populated later!</p>
                              <div style={{position: "absolute", width: 48, height: 48, left: 217, top: 217, border: '1px solid #364152', borderRadius: 'var(--radius-l)'}}></div>
                              <svg viewBox="0 0 25 24" style={{position: "absolute", left: 229, top: 230}} width="25" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21.5 21L18.0001 17.5M20.5 11.5C20.5 16.1944 16.6944 20 12 20C7.30558 20 3.5 16.1944 3.5 11.5C3.5 6.80558 7.30558 3 12 3C16.6944 3 20.5 6.80558 20.5 11.5Z" stroke="#CDD5DF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <svg width="480" height="480" viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="240" cy="240" r="47.5" stroke="#202939"/>
                                <circle cx="240" cy="240" r="79.5" stroke="#202939"/>
                                <circle cx="240" cy="240" r="111.5" stroke="#202939"/>
                                <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.5} />
                                <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.4} />
                                <circle cx="240" cy="240" r="175.5" stroke="#202939" opacity={0.3} />
                                <circle cx="240" cy="240" r="207.5" stroke="#202939" opacity={0.2} />
                                <circle cx="240" cy="240" r="239.5" stroke="#202939" opacity={0.1} />
                              </svg>
                            </div>
                          </Box>
                      </TableCell>
                    </TableRow>
                  }
                  </TableBody>
                </Table>
            </TableContainer>
           </>}
        </Card>);
}
 
export default SOCReportTable;